
import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Building2, BookOpen, Rocket, Users } from "lucide-react";

interface OperationsLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
}

const OperationsLayout = ({ children, title, subtitle }: OperationsLayoutProps) => {
  const location = useLocation();

  const navigationItems = [
    { path: "/operations/sops", label: "SOP Library", icon: BookOpen },
    { path: "/operations/launch", label: "Launch Kit", icon: Rocket },
    { path: "/operations/hub", label: "Partner Hub", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream-white to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-white to-juv-soft-blush border-b border-juv-rose-clay/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-juv-gradient-primary rounded-full flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-juv-deep-chestnut font-juv-heading">JUV Operations</h1>
                <p className="text-sm text-juv-deep-chestnut/70 font-juv-body">Franchise & Partner Portal</p>
              </div>
            </div>
            <Link to="/dashboard">
              <Button 
                variant="outline" 
                className="border-juv-rose-clay/50 text-juv-deep-chestnut hover:bg-juv-soft-blush/50 transition-all duration-300"
              >
                Back to Glow App
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-juv-rose-clay/20">
        <div className="container mx-auto px-6">
          <div className="flex space-x-8">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 py-4 border-b-2 transition-all duration-300 ${
                    isActive
                      ? "border-juv-terracotta text-juv-terracotta shadow-sm"
                      : "border-transparent text-juv-deep-chestnut/70 hover:text-juv-deep-chestnut hover:border-juv-rose-clay/50"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium font-juv-heading">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-juv-deep-chestnut font-juv-heading">{title}</h2>
          {subtitle && <p className="text-lg text-juv-deep-chestnut/70 mt-2 font-juv-body">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
};

export default OperationsLayout;
